import { Flex, Icon, VStack, Heading, Text, Box, HStack, Separator } from "@chakra-ui/react"
import { AssetInfo, OperationalStatus, SystemNodeAttributes } from "../../../types/SystemView"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { IconType } from "react-icons"
import { useLazyQuery } from "@apollo/client"
import { IoTDataLatestDocument } from "@hydrologiq/gql/graphql"
import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { DateTime } from "timezonecomplete"
import { IANATimeZone, toTimestamp } from "@hydrologiq/common/helpers/date"
import { energy } from "@hydrologiq/common/transformations/data/energy"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import AssetLink from "@hydrologiq/shared/asset/AssetLink"
import { Asset } from "@hydrologiq/gql-experience/graphql"
import { useLiveValue } from "../../../providers/helpers/useSystem"

export interface ApiAssetAssignment {
  id: string
  status: "active" | "inactive" | null
  monitoringInstanceId?: string
}

export interface ApiAssetAttributeProps extends SystemNodeAttributes {
  installation?: string
  start: DateTime
  end: DateTime
  offline?: boolean
}

export interface ApiAssetImplProps {
  attributes?: ApiAssetAttributeProps[]
  statusToggle: () => void
  icon?: IconType
  name: string
  disabled?: boolean
  assignment?: ApiAssetAssignment
  assignmentStatus: string
  operationalStatus?: OperationalStatus
  workflowState: string
  controllable?: boolean
  asset?: AssetInfo
  installation?: string
}

function ApiAssetAttribute(props: ApiAssetAttributeProps) {
  const { name, unit, field, slot, attribute, installation, asset, start, end, thresholds, installationOnly } = props
  let { value } = props

  // Subscribe to the live value from the store (45s poll, leaf-level re-render only)
  const liveValue = useLiveValue(asset?.id, field)
  const serverProvided = liveValue !== undefined

  const [getLatestValue, { data, error }] = useLazyQuery(IoTDataLatestDocument)
  const { pathname } = useLocation()

  useEffect(() => {
    if (!serverProvided && field && start && end) {
      getLatestValue({
        variables: {
          installations: asset && !installationOnly ? "" : installation ?? "",
          assets: asset?.id || "",
          start: toTimestamp(start),
          stop: toTimestamp(end),
          field: field,
          location: IANATimeZone(),
        },
        pollInterval: 30000,
      })
    }
  }, [getLatestValue, installation, asset, start, end, pathname, serverProvided])

  if (serverProvided) {
    if (liveValue !== null && liveValue !== undefined) {
      let floatValue = liveValue
      if (field?.includes("power")) floatValue = energy(EnergyUnit.W, EnergyUnit.kW, floatValue)
      value = floatValue.toFixed(2)
    } else value = "0"
  } else if (data) {
    if (data.getIoTDataLatest && data.getIoTDataLatest.value) {
      let floatValue = parseFloat(data.getIoTDataLatest.value.toString())
      if (field?.includes("power")) floatValue = energy(EnergyUnit.W, EnergyUnit.kW, floatValue)
      value = floatValue.toFixed(2)
    } else value = "0"
  }
  if (error) value = "0"
  if (attribute) {
    if (attribute === "location") value = asset?.location?.name ?? "n/a"
  }
  if (!value) value = "0"

  const valueInt = parseInt(value)
  const applyThresholds = (data || serverProvided) && thresholds && typeof valueInt === "number"

  const inThreshold = (min?: number, max?: number) =>
    min && max ? valueInt >= min && valueInt < max : min ? valueInt >= min : max ? valueInt <= max : false
  const criticalThreshold = inThreshold(undefined, thresholds?.critical) ? "#BF0000" : undefined
  const lowThreshold = inThreshold(undefined, thresholds?.low) ? "#a34a0f" : undefined
  const okThreshold = inThreshold(undefined, thresholds?.ok) ? "#BF9700" : undefined
  const highThreshold = inThreshold(undefined, thresholds?.high) ? "#80b738" : undefined
  const peakThreshold = inThreshold(thresholds?.peak) ? "#00BF00" : undefined

  const backgroundColor = criticalThreshold || lowThreshold || okThreshold || highThreshold || peakThreshold
  const textColor = lowThreshold || okThreshold ? "white" : undefined

  return (
    <HStack
      fontSize="8px"
      width="100%"
      justifyContent={[0, 3].includes(slot) ? "flex-end" : slot === 2 ? "center" : "flex-start"}
    >
      <HStack background={backgroundColor} color={textColor} borderRadius="3px" padding="0 2px" gap={0.5}>
        <Text>{name}:</Text>
        <HStack fontWeight="bold" gap={0.45}>
          <Tooltip content={value} positioning={{ placement: "bottom-end" }}>
            <Text truncate maxWidth="50px">
              {value}
            </Text>
          </Tooltip>
          <Text>{unit}</Text>
        </HStack>
      </HStack>
    </HStack>
  )
}

function ApiAssetImpl(props: ApiAssetImplProps) {
  const {
    attributes,
    statusToggle,
    icon,
    name,
    disabled,
    assignment,
    assignmentStatus,
    workflowState,
    controllable,
    asset,
    operationalStatus,
    installation,
  } = props

  const attributesSlots = (attributes ?? []).reduce((acc, attribute) => {
    const slot = attribute.slot
    if (typeof slot === "number") {
      acc[slot] = attribute
    }
    return acc
  }, {} as Record<number, ApiAssetAttributeProps>)

  const isActive = assignmentStatus === "active" || (operationalStatus && operationalStatus === "active")
  const anyAttributesOffline = Object.values(attributesSlots).some((attribute) => attribute.offline)

  const Attribute = ({ slot }: { slot: number }) => {
    const attribute = attributesSlots[slot]
    if (attribute && (isActive || attribute.offline)) {
      return <ApiAssetAttribute {...attribute} asset={asset} installation={installation} />
    }
    return null
  }


  const isAssetsSectionUsed = useIsSectionEnabled("assets")

  return (
    <Box width="175px" height="80px" color="primary">
      <VStack height={controllable ? "55px" : "35px"} gap={"3px"} width="100%" padding="3px 5px">
        <HStack justifyContent="space-between" width={"100%"} gap={0.25}>
          <Flex width="100%" height="25px" gap={"5px"} alignItems="center">
            <Icon height="15px" width="15px" as={icon} />
            {isAssetsSectionUsed ? <span className="nodrag"><AssetLink truncate={true} maxWidth={isActive && 0 in attributesSlots ? "60px" : "115px"} key={`${asset?.id}-${name}-${isActive}`} asset={{ id: asset?.id || "", name: name || "", monitoring_status: isActive ? "active" : "inactive" } as Asset} size="9px" iconSize={9.5} openExternal={true} tooltip={true} /></span> : <Heading as={"h3"} fontSize="9px" truncate maxWidth={isActive && 0 in attributesSlots ? "90px" : "145px"}>
              {name}
            </Heading>}
          </Flex>
          <Attribute slot={0} />
        </HStack>
        {/* Show toggle button only if assignment is provided */}
        {controllable && assignment && assignment.monitoringInstanceId && (
          <HStack width="100%" alignItems="center" gap={3} justifyContent="center">
            <Switch
              onClick={(e) => {
                e.preventDefault()
                if (!disabled) {
                  statusToggle()
                }
              }}
              checked={isActive}
              defaultChecked={false}
              disabled={disabled || workflowState === "processing" || workflowState === "confirming"}
              inputProps={{
                "aria-label": isActive ? "Deactivate" : "Activate",
              }}
            />
            <Text fontSize="10px">{isActive ? "Monitoring ON" : "Monitoring OFF"}</Text>
          </HStack>
        )}
      </VStack>
      {(isActive || anyAttributesOffline) && <Separator width="100%" borderColor="white" />}
      <HStack justifyContent="space-between" width={"100%"} height="20px" padding="3px 5px">
        <Attribute slot={1} />
        <Attribute slot={2} />
        <Attribute slot={3} />
      </HStack>
    </Box>
  )
}

export default ApiAssetImpl
